/**
 * PlatformShell - Layout for platform-level pages.
 *
 * Used for pages that are NOT workspace-scoped:
 * - /dashboard (Platform Dashboard)
 * - /clients (Clients list)
 * - /settings (Account Settings)
 * - /help (Global Help)
 *
 * Structure:
 * - Sidebar on the left (unified navigation rail; portfolio mode)
 * - Main content area
 *
 * Design principles:
 * 1. Consistent global navigation
 * 2. No workspace context displayed
 * 3. Clean, focused content area
 */

import clsx from "clsx";
import React from "react";
import { Outlet } from "react-router-dom";

import { Sidebar } from "./Sidebar";

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export interface PlatformShellProps {
  children?: React.ReactNode;
}

export function PlatformShell({ children }: PlatformShellProps) {
  return (
    <div
      className={clsx(
        "min-h-screen",
        "ws-text",
        "bg-gradient-to-b from-[var(--ws-bg-gradient-from)] via-[var(--ws-bg)] to-[var(--ws-bg-gradient-to)]",
      )}
    >
      {/* URL canonicalization (?client= stripping) is mounted once at the
          AppRoutes root, not per-shell. */}
      <div className="flex min-h-screen">
        {/* Unified Navigation Sidebar */}
        <Sidebar />

        {/* Page Content */}
        <main className="flex-1 min-w-0">
          <div
            className={clsx(
              "mx-auto max-w-[var(--ws-max-content)]",
              "px-4 py-6",
              "sm:px-6 lg:px-8 lg:py-8",
            )}
          >
            {children || <Outlet />}
          </div>
        </main>
      </div>
    </div>
  );
}

export default PlatformShell;
